import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { RecoveryCodesResponse, UserSettingsService } from '../../../../../../services/members/user-settings.service';
import { GrowlerService } from '../../../../../shared/growler/growler.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { Membership } from '../../../../../../models/entities/membership';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatDialog as MatDialogV2 } from '@angular/material/dialog';
import { RecoveryCodesDialogComponent } from '../../../../../v2-layout/shared/recovery-codes-dialog/recovery-codes-dialog.component';
import { SetupGoogleAuthenticatorComponent } from './setup-google-authenticator/setup-google-authenticator.component';
import { GenericDialogResult } from '../../../../shared/generic-mat-dialog/generic-dialog-result';
import { SetupSmsAuthenticationComponent } from './setup-sms-authentication/setup-sms-authentication.component';
import { Confirm2faCodeComponent, ConfirmTwoFactorInput, ConfirmTwoFactorOutput } from '../confirm-2fa-code/confirm-2fa-code.component';
import { JwtLegFiClaims } from '../../../../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../../../../services/auth/legfi-jwt.service';
import { MembersService } from '../../../../../../services/members/members.service';
import { HostListenerService } from '../../../../../../services/host-listener.service';
import { CardSkeletonLoaderComponent } from '../../../../../shared/card-skeleton-loader/card-skeleton-loader.component';
import { SuperUser2faGuard } from '../../../../../../common/auth/super-user-2fa.guard';
import { MatDialogSizes } from 'app/enums/mat-dialog-sizes.enum';
import { SharedModule } from '../../../../../shared/shared.module';
import { SimpleBadgeComponent } from '../../../../../v2-layout/shared/simple-badge/simple-badge.component';
import { ComparisonValidatorsModule } from '../../../../../../directives/validators/comparison/comparison-validators.module';
import { StringValidatorsModule } from '../../../../../../directives/validators/string/string-validators.module';

@UntilDestroy()
@Component({
    selector: 'app-two-factor-auth',
    templateUrl: './two-factor-auth.component.html',
    styleUrls: ['./two-factor-auth.component.scss'],
    standalone: true,
    imports: [
        // Shared
        SharedModule,
        SimpleBadgeComponent,
        CardSkeletonLoaderComponent,
        // Dependencies
        ComparisonValidatorsModule,
        StringValidatorsModule,
    ],
})
export class TwoFactorAuthComponent implements OnInit
{
    @Input() highlight2fa = false;
    /**
     * Compact/embedded presentation: hides this widget's own page heading and intro
     * paragraph (the host surface — e.g. the 2FA gate dialog — supplies its own title
     * and copy) and tightens the card layout. Default false keeps the Settings → Security
     * tab unchanged.
     */
    @Input() compact = false;
    @Output() enrollmentChecked = new EventEmitter<boolean>();
    /**
     * Compact/embedded mode only: a method's "Setup" was tapped. The host (the 2FA gate
     * stepper) swaps to the inline setup step instead of this widget opening a nested dialog.
     * Non-compact (Settings) ignores this and opens its dialog as before.
     */
    @Output() setupRequested = new EventEmitter<'google' | 'sms'>();

    isSmsAuthEnabled = false;
    smsLast4 = '';
    isGoogleAuthEnabled = false;
    isLoading = true;

    /** In-flight guard for the regenerate call — the request sits between the re-auth dialog
     *  closing and the show-once dialog opening, which would otherwise be silent. */
    isRegenerating = false;
    jwt: JwtLegFiClaims;

    /** True on small breakpoints — condenses the compact/embedded cards (drops the verbose method blurb). */
    readonly isMobileMode$ = this._host.isMobileMode();

    constructor(
            private _userSettings: UserSettingsService,
            private _membershipService: MembersService,
            private _growler: GrowlerService,
            private _dialog: MatDialog,
            private _v2Dialog: MatDialogV2,
            private _superUser2faGuard: SuperUser2faGuard,
            private _host: HostListenerService,
    ) {
    }

    ngOnInit(): void {
        this.jwt = LegFiJwtService.read();
        this.loadAuthenticatorSettings();
    }

    setupGoogleAuth() {
        // In the gate stepper, hand off to the inline setup step (no nested dialog).
        if (this.compact) {
            this.setupRequested.emit('google');
            return;
        }
        this._dialog.open(SetupGoogleAuthenticatorComponent, {
            width: MatDialogSizes.SM,
            panelClass: ['two-factor-flow-dialog'],
            backdropClass: 'two-factor-flow-backdrop',
        }).afterClosed().subscribe({
            next: (result: GenericDialogResult) => {
                if (result?.success) {
                    this.loadAuthenticatorSettings();
                }
            },
        });
    }

    setupSmsAuth() {
        // In the gate stepper, hand off to the inline setup step (no nested dialog).
        if (this.compact) {
            this.setupRequested.emit('sms');
            return;
        }
        this._dialog.open(SetupSmsAuthenticationComponent, {
            width: MatDialogSizes.SM,
            panelClass: ['two-factor-flow-dialog'],
            backdropClass: 'two-factor-flow-backdrop',
        }).afterClosed().subscribe({
            next: (result: GenericDialogResult) => {
                if (result?.success) {
                    this.loadAuthenticatorSettings();
                }
            },
        });
    }

    disableSmsAuth() {
        // Annotated const rather than a cast, so excess-property checking applies.
        const data: ConfirmTwoFactorInput = {
            type: 'sms',
            canGoBack: false,
        };

        this._dialog.open(Confirm2faCodeComponent, {
            width: MatDialogSizes.SM,
            panelClass: ['v2-dialog', 'two-factor-flow-dialog'],
            backdropClass: 'two-factor-flow-backdrop',
            data,
        }).afterClosed().subscribe({
            next: (result: ConfirmTwoFactorOutput) => {
                if (result?.success) {
                    this._userSettings.removeSmsAuth(result.token)
                            .subscribe({
                                next: () => {
                                    this.loadAuthenticatorSettings();
                                    this._growler.success('Success', 'SMS Authentication has been removed from your account.');
                                },
                            });
                }
            },
        });
    }

    disableGoogleAuth() {
        // Annotated const rather than a cast, so excess-property checking applies.
        const data: ConfirmTwoFactorInput = {
            type: 'google',
            canGoBack: false,
        };

        this._dialog.open(Confirm2faCodeComponent, {
            width: MatDialogSizes.SM,
            panelClass: ['v2-dialog', 'two-factor-flow-dialog'],
            backdropClass: 'two-factor-flow-backdrop',
            data,
        }).afterClosed().subscribe({
            next: (result: ConfirmTwoFactorOutput) => {
                if (result?.success) {
                    this._userSettings.removeGoogleAuth(result.token)
                            .subscribe({
                                next: () => {
                                    this.loadAuthenticatorSettings();
                                    this._growler.success('Success', 'Google Authenticator has been removed from your account.');
                                },
                            });
                }
            },
        });
    }

    /**
     * Regenerate the user's recovery codes (SD-8082). Requires a fresh 2FA re-authentication
     * (reusing the Confirm2faCodeComponent flow, like the disable actions), then shows the new
     * codes exactly once. Only available while at least one 2FA method is enrolled.
     */
    regenerateRecoveryCodes() {
        const type: 'google' | 'sms' = this.isGoogleAuthEnabled ? 'google' : 'sms';

        // Annotated const rather than a cast, so excess-property checking applies.
        const data: ConfirmTwoFactorInput = {
            type,
            canGoBack: false,
        };

        this._dialog.open(Confirm2faCodeComponent, {
            width: MatDialogSizes.SM,
            panelClass: ['v2-dialog', 'two-factor-flow-dialog'],
            backdropClass: 'two-factor-flow-backdrop',
            data,
        }).afterClosed().subscribe({
            next: (result: ConfirmTwoFactorOutput) => {
                if (result?.success) {
                    this.isRegenerating = true;
                    this._userSettings.regenerateRecoveryCodes(result.type, result.token)
                            .pipe(
                                    untilDestroyed(this),
                                    finalize(() => this.isRegenerating = false),
                            )
                            .subscribe({
                                next: (response: RecoveryCodesResponse) => {
                                    if (!response.recoveryCodes?.length) {
                                        this._growler.error('Error', 'No recovery codes were generated. Please try again.');
                                        return;
                                    }
                                    RecoveryCodesDialogComponent.open(this._v2Dialog, response.recoveryCodes);
                                    this._growler.success('Success', 'Your recovery codes have been regenerated. Your previous codes no longer work.');
                                },
                                error: () => {
                                    this._growler.error('Error', 'We could not regenerate your recovery codes. Please try again.');
                                },
                            });
                }
            },
        });
    }

    loadAuthenticatorSettings() {
        this.isLoading = true;
        this._membershipService.getMember(this.jwt.orgId, this.jwt.memberId).pipe(
            untilDestroyed(this),
            finalize(() => this.isLoading = false),
        ).subscribe({
            next: (membership: Membership) => {
                if (membership.user.isSmsAuthVerificationEnrolled) {
                    this.isSmsAuthEnabled = true;
                    this.smsLast4 = membership.user.smsLast4;
                } else {
                    this.isSmsAuthEnabled = false;
                    this.smsLast4 = '';
                }
                this.isGoogleAuthEnabled = membership.user.isGoogleAuthVerificationEnrolled;

                const enrolled = this.isSmsAuthEnabled || this.isGoogleAuthEnabled;
                if (enrolled) {
                    this._superUser2faGuard.clearCache();
                }
                this.enrollmentChecked.emit(enrolled);
            },
        });
    }
}
